import React, { useEffect, useState } from 'react';
import {
  CreativeGroupSuggestion,
  PLATFORM_LABELS,
  createCreativeGroup,
  dismissCreativeGroupSuggestion,
  getCreativeGroupSuggestions,
} from '../../../services/cgBoostApi';
import { Pill } from '../../ui';
import InlineErrorBanner from '../InlineErrorBanner';

interface Props {
  campaignId: string;
  /** Bumped by parent on every refetch so the panel re-pulls suggestions. */
  refreshKey: number;
  onChanged: () => void;
}

const CreativeGroupSuggestionsPanel: React.FC<Props> = ({ campaignId, refreshKey, onChanged }) => {
  const [suggestions, setSuggestions] = useState<CreativeGroupSuggestion[] | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCreativeGroupSuggestions(campaignId)
      .then((s) => {
        if (!cancelled) setSuggestions(s);
      })
      .catch((err) => {
        console.warn('[CreativeGroupSuggestions] load failed:', err);
        if (!cancelled) setSuggestions([]);
      });
    return () => { cancelled = true; };
  }, [campaignId, refreshKey]);

  if (!suggestions || suggestions.length === 0) return null;

  const apply = async (s: CreativeGroupSuggestion): Promise<void> => {
    setPendingId(s.suggestionId);
    setMutationError(null);
    try {
      await createCreativeGroup(campaignId, s.candidateAssetIds);
      onChanged();
    } catch (err: any) {
      console.warn('[CreativeGroupSuggestions] createCreativeGroup failed:', err);
      setMutationError(err?.message ?? 'Could not create the group. Please retry.');
    } finally {
      setPendingId(null);
    }
  };

  const dismiss = async (s: CreativeGroupSuggestion): Promise<void> => {
    setPendingId(s.suggestionId);
    setMutationError(null);
    try {
      await dismissCreativeGroupSuggestion(campaignId, s.suggestionId);
      onChanged();
    } catch (err: any) {
      console.warn('[CreativeGroupSuggestions] dismiss failed:', err);
      setMutationError(err?.message ?? 'Could not dismiss the suggestion. Please retry.');
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div style={panelStyle}>
      <button onClick={() => setExpanded((v) => !v)} style={headerStyle}>
        <span style={{ fontWeight: 600 }}>
          💡 {suggestions.length} suggested {suggestions.length === 1 ? 'grouping' : 'groupings'}
        </span>
        <span style={{ color: 'var(--fg-3)', fontSize: 12 }}>
          {expanded ? 'Hide' : 'Review'}
        </span>
      </button>
      {expanded && mutationError && (
        <InlineErrorBanner
          message={mutationError}
          onDismiss={() => setMutationError(null)}
          size="sm"
          style={{ marginTop: 8 }}
        />
      )}
      {expanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          {suggestions.map((s) => {
            const pending = pendingId === s.suggestionId;
            return (
              <div key={s.suggestionId} style={cardStyle}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ fontWeight: 600 }}>
                    {s.creatorName} <span style={{ color: 'var(--fg-3)', fontWeight: 400 }}>· {s.format}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--fg-3)' }}>
                    Same creator on {s.platforms.map((p) => PLATFORM_LABELS[p]).join(' + ')}
                    {' · '}
                    <span style={{ fontStyle: 'italic' }}>{s.confidence === 'high' ? 'high' : 'medium'} confidence</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Pill tone="success" size="sm" disabled={pending} onClick={() => apply(s)}>
                    Group
                  </Pill>
                  <Pill tone="neutral" size="sm" disabled={pending} onClick={() => dismiss(s)}>
                    Dismiss
                  </Pill>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const panelStyle: React.CSSProperties = {
  background: 'var(--bg-hover)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 6,
  padding: 10,
  marginBottom: 12,
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  fontSize: 13,
  color: 'var(--fg-1)',
};

const cardStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 10,
  padding: 10,
  background: 'var(--bg-surface-1)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 4,
};

export default CreativeGroupSuggestionsPanel;
